import { StepType } from "@telarya/telarya-sdk"
import { shallowRef } from "vue"

import { StepTelarTypeToDocumentTypes } from "@/components/Documents/StepTelarTypeToDocumentTypes"
import reportError from "@/utils/reportError"

const CACHE_VERSION = 1
const CACHE_KEY_PREFIX = `step-telar-document-requirements:v${CACHE_VERSION}:`

export const stepTelarDocumentRequirementsEffectiveConfig = shallowRef(null)
export const stepTelarDocumentRequirementsCompanyId = shallowRef(null)
export const stepTelarDocumentRequirementsLastError = shallowRef(null)

export const normalizeStepTypeForDocumentRequirements = (stepType) => {
  if (stepType === StepType.PickupEmpty || stepType === StepType.PickupFull) return StepType.Pickup
  if (stepType === StepType.DropoffEmpty || stepType === StepType.DropoffFull) return StepType.Dropoff
  return stepType
}

const buildCacheKey = (companyId) => `${CACHE_KEY_PREFIX}${companyId}`

const parseRequirement = (raw) => {
  if (raw == null || typeof raw !== "object") return null

  const documentType = raw.documentType ?? raw.DocumentType
  if (typeof documentType !== "string" || documentType.length === 0) return null

  const isRequired = raw.isRequired ?? raw.IsRequired

  return {
    documentType,
    isRequired: isRequired === true
  }
}

const parseEffectiveConfig = (raw) => {
  if (raw == null || typeof raw !== "object") return null

  const result = {}

  for (const [stepType, telarMapRaw] of Object.entries(raw)) {
    if (telarMapRaw == null || typeof telarMapRaw !== "object") continue

    const telarMap = {}

    for (const [telarType, requirementsRaw] of Object.entries(telarMapRaw)) {
      if (!Array.isArray(requirementsRaw)) continue

      const dedup = new Map()
      for (const reqRaw of requirementsRaw) {
        const req = parseRequirement(reqRaw)
        if (!req) continue

        const existing = dedup.get(req.documentType)
        dedup.set(req.documentType, (existing?.isRequired ?? false) || req.isRequired)
      }

      telarMap[telarType] = Array.from(dedup.entries()).map(([documentType, isRequired]) => ({
        documentType,
        isRequired
      }))
    }

    result[stepType] = telarMap
  }

  return result
}

export const getStepTelarDocumentRequirements = (stepType, telarType) => {
  const normalizedStepType = normalizeStepTypeForDocumentRequirements(stepType)

  const config = stepTelarDocumentRequirementsEffectiveConfig.value ?? StepTelarTypeToDocumentTypes
  const requirements = config?.[normalizedStepType]?.[telarType] ?? []

  return requirements.map((r) => ({
    documentType: r.documentType,
    isRequired: r.isRequired === true
  }))
}

export const hydrateStepTelarDocumentRequirementsConfigFromLocalStorage = (companyId) => {
  if (!companyId) return

  if (stepTelarDocumentRequirementsCompanyId.value && stepTelarDocumentRequirementsCompanyId.value !== companyId) {
    stepTelarDocumentRequirementsEffectiveConfig.value = null
  }

  stepTelarDocumentRequirementsCompanyId.value = companyId

  const cacheKey = buildCacheKey(companyId)
  const raw = typeof window !== "undefined" ? window.localStorage.getItem(cacheKey) : null
  if (!raw) return

  try {
    const parsed = JSON.parse(raw)
    const config = parseEffectiveConfig(parsed?.data ?? parsed)
    if (!config) return

    stepTelarDocumentRequirementsEffectiveConfig.value = config
  } catch (error) {
    reportError(error, { context: "StepTelarDocumentRequirementsConfigService.hydrate", companyId })
    // Ignore corrupted cache; fall back to bundled defaults.
  }
}

export const refreshStepTelarDocumentRequirementsConfigFromNetwork = async (JobsServiceClient, companyId) => {
  if (!companyId) return null

  if (!JobsServiceClient?.getStepTelarDocumentRequirements) {
    throw new Error("JobsServiceClient.getStepTelarDocumentRequirements is not available")
  }

  if (stepTelarDocumentRequirementsCompanyId.value && stepTelarDocumentRequirementsCompanyId.value !== companyId) {
    stepTelarDocumentRequirementsEffectiveConfig.value = null
  }
  stepTelarDocumentRequirementsCompanyId.value = companyId

  const sdkResponse = await JobsServiceClient.getStepTelarDocumentRequirements()
  const json = typeof sdkResponse?.toJSON === "function" ? sdkResponse.toJSON() : sdkResponse
  const config = parseEffectiveConfig(json)
  if (!config) {
    throw new Error("Failed to parse step/telar document requirements config response")
  }

  stepTelarDocumentRequirementsEffectiveConfig.value = config

  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(buildCacheKey(companyId), JSON.stringify({ v: CACHE_VERSION, data: config }))
    } catch (error) {
      reportError(error, {
        context: "StepTelarDocumentRequirementsConfigService.cache",
        companyId
      })
      // Best-effort cache.
    }
  }

  return config
}

export const initializeStepTelarDocumentRequirementsConfig = async (JobsServiceClient, companyId) => {
  hydrateStepTelarDocumentRequirementsConfigFromLocalStorage(companyId)
  try {
    stepTelarDocumentRequirementsLastError.value = null
    await refreshStepTelarDocumentRequirementsConfigFromNetwork(JobsServiceClient, companyId)
  } catch (e) {
    reportError(e, { context: "StepTelarDocumentRequirementsConfigService.initialize", companyId })
    stepTelarDocumentRequirementsLastError.value = e
  }
}
